import { DateTime } from 'luxon';

import type { Client, Project, Task } from '@/features/tasks/types';

/**
 * Backlog ordering for the Plan sidebar.
 *
 * Planning is a deadline-driven act, so the default is not "sort by due date"
 * but "group by how soon it is due". A flat ascending sort answers *which* is
 * sooner; a group header answers *whether it matters today*, which is the
 * question actually being asked while dragging chips onto a week.
 */

export type BacklogSort = 'due' | 'effort' | 'client' | 'project' | 'priority' | 'completed';

export const BACKLOG_SORTS: { value: BacklogSort; label: string }[] = [
  { value: 'due', label: 'Due date' },
  { value: 'effort', label: 'Effort' },
  { value: 'priority', label: 'Priority' },
  { value: 'client', label: 'Client' },
  { value: 'project', label: 'Project' },
];

/** Grouping options when reviewing finished work. */
export const COMPLETED_BACKLOG_SORTS: { value: BacklogSort; label: string }[] = [
  { value: 'completed', label: 'Recently completed' },
  { value: 'client', label: 'Client' },
  { value: 'project', label: 'Project' },
];

/** Urgency tiers. Ordered: lower `rank` sorts first. */
export type DueTone = 'overdue' | 'today' | 'tomorrow' | 'soon' | 'later' | 'none';

const TONE_RANK: Record<DueTone, number> = {
  overdue: 0,
  today: 1,
  tomorrow: 2,
  soon: 3,
  later: 4,
  none: 5,
};

/**
 * Due-pill classes, shared by every surface that renders a task.
 *
 * Colour only reinforces what the words already say, so the pill stays
 * readable when colour is not available to the reader.
 */
export const DUE_PILL: Record<DueTone, string> = {
  overdue: 'bg-[var(--danger-quiet)] text-danger',
  today: 'bg-[var(--warning-quiet)] text-warning',
  tomorrow: 'bg-[var(--accent-quiet)] text-accent',
  soon: 'text-text-secondary',
  later: 'text-text-tertiary',
  none: '',
};

export interface DueInfo {
  tone: DueTone;
  /** Short form for a chip: `Overdue 2d`, `Due 5:00 PM`, `Due Fri`. */
  label: string;
  /** Long form for tooltips and detail bars: `Due Fri, Mar 6 at 5:00 PM`. */
  full: string;
  at: DateTime | null;
}

const NO_DUE: DueInfo = { tone: 'none', label: '', full: 'No due date', at: null };

/**
 * Classify a due date relative to "now" in the user's zone.
 *
 * Day boundaries, not 24-hour offsets: something due at 9am tomorrow is
 * "tomorrow" whether it is now 8am or 11pm tonight.
 */
export function dueInfo(
  due: string | null,
  timezone: string,
  now: DateTime = DateTime.now(),
): DueInfo {
  if (!due) return NO_DUE;

  const at = DateTime.fromISO(due, { zone: 'utc' }).setZone(timezone);
  if (!at.isValid) return NO_DUE;

  const zoneNow = now.setZone(timezone);
  const full = `Due ${at.toFormat('ccc, LLL d')} at ${at.toFormat('h:mm a')}`;
  const days = at.startOf('day').diff(zoneNow.startOf('day'), 'days').days;

  if (at < zoneNow) {
    const late = Math.round(Math.abs(days));
    return {
      tone: 'overdue',
      label: late >= 1 ? `Overdue ${late}d` : 'Overdue',
      full,
      at,
    };
  }
  if (days === 0) return { tone: 'today', label: `Due ${at.toFormat('h:mm a')}`, full, at };
  if (days === 1) return { tone: 'tomorrow', label: 'Due tomorrow', full, at };
  if (days <= 6) return { tone: 'soon', label: `Due ${at.toFormat('ccc')}`, full, at };
  return { tone: 'later', label: `Due ${at.toFormat('LLL d')}`, full, at };
}

/** True when a block placed at `endsAt` finishes after the task is due. */
export function isLate(due: string | null, endsAt: string | Date): boolean {
  if (!due) return false;
  const dueAt = DateTime.fromISO(due, { zone: 'utc' });
  if (!dueAt.isValid) return false;
  const end =
    endsAt instanceof Date
      ? DateTime.fromJSDate(endsAt)
      : DateTime.fromISO(endsAt, { zone: 'utc' });
  return end.toUTC() > dueAt.toUTC();
}

/** `Scheduled 3h after it is due`: how the warning explains itself. */
export function lateBy(due: string, endsAt: string | Date, timezone: string): string {
  const dueAt = DateTime.fromISO(due, { zone: 'utc' }).setZone(timezone);
  const end = (
    endsAt instanceof Date ? DateTime.fromJSDate(endsAt) : DateTime.fromISO(endsAt, { zone: 'utc' })
  ).setZone(timezone);
  const minutes = Math.max(1, Math.round(end.diff(dueAt, 'minutes').minutes));
  if (minutes < 60) return `${minutes}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

const PRIORITY_RANK: Record<Task['priority'], number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
};

const EFFORT_BUCKETS: { max: number; label: string }[] = [
  { max: 15, label: 'Under 15m' },
  { max: 30, label: '15–30m' },
  { max: 60, label: '30m–1h' },
  { max: 180, label: '1–3h' },
  { max: Infinity, label: 'Over 3h' },
];

export interface BacklogGroup {
  key: string;
  label: string;
  /** Right-aligned count/summary in the group header. */
  meta: string;
  tone: DueTone | 'plain';
  /** Client swatch, when grouping by client. */
  color?: string;
  tasks: Task[];
}

const DUE_GROUP_LABEL: Record<DueTone, string> = {
  overdue: 'Overdue',
  today: 'Due today',
  tomorrow: 'Due tomorrow',
  soon: 'Later this week',
  later: 'Later',
  none: 'No due date',
};

export type CompletedTone = 'today' | 'yesterday' | 'this_week' | 'this_month' | 'older' | 'unknown';

const COMPLETED_TONE_RANK: Record<CompletedTone, number> = {
  today: 0,
  yesterday: 1,
  this_week: 2,
  this_month: 3,
  older: 4,
  unknown: 5,
};

const COMPLETED_GROUP_LABEL: Record<CompletedTone, string> = {
  today: 'Completed today',
  yesterday: 'Completed yesterday',
  this_week: 'Earlier this week',
  this_month: 'Earlier this month',
  older: 'Older',
  unknown: 'Unknown date',
};

export interface CompletedInfo {
  tone: CompletedTone;
  label: string;
  full: string;
  at: DateTime | null;
}

const NO_COMPLETED: CompletedInfo = {
  tone: 'unknown',
  label: '',
  full: 'Completion date unknown',
  at: null,
};

/** Classify when a task was finished, for history views. */
export function completedInfo(
  completedAt: string | null,
  timezone: string,
  now: DateTime = DateTime.now(),
): CompletedInfo {
  if (!completedAt) return NO_COMPLETED;

  const at = DateTime.fromISO(completedAt, { zone: 'utc' }).setZone(timezone);
  if (!at.isValid) return NO_COMPLETED;

  const zoneNow = now.setZone(timezone);
  const full = `Completed ${at.toFormat('ccc, LLL d')} at ${at.toFormat('h:mm a')}`;
  const days = zoneNow.startOf('day').diff(at.startOf('day'), 'days').days;

  if (days === 0) {
    return { tone: 'today', label: `Done ${at.toFormat('h:mm a')}`, full, at };
  }
  if (days === 1) {
    return { tone: 'yesterday', label: 'Done yesterday', full, at };
  }
  if (days <= 6) {
    return { tone: 'this_week', label: `Done ${at.toFormat('ccc')}`, full, at };
  }
  if (at >= zoneNow.startOf('month')) {
    return { tone: 'this_month', label: `Done ${at.toFormat('LLL d')}`, full, at };
  }
  return { tone: 'older', label: `Done ${at.toFormat('LLL d, yyyy')}`, full, at };
}

function totalMinutes(tasks: Task[]): number {
  return tasks.reduce((sum, t) => sum + (t.estimate_minutes ?? 0), 0);
}

function compactDuration(minutes: number): string {
  if (minutes <= 0) return '-';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Group + order the backlog for one sort mode.
 *
 * Every mode groups. Ungrouped lists make the user infer the rule from the
 * order; a header states it.
 */
export function groupBacklog(
  tasks: Task[],
  sort: BacklogSort,
  ctx: {
    timezone: string;
    clients: Client[];
    projects: Project[];
    now?: DateTime;
  },
): BacklogGroup[] {
  const now = ctx.now ?? DateTime.now();
  const info = new Map<number, DueInfo>();
  for (const t of tasks) info.set(t.id, dueInfo(t.due_at, ctx.timezone, now));

  const completed = new Map<number, CompletedInfo>();
  if (sort === 'completed') {
    for (const t of tasks) {
      completed.set(t.id, completedInfo(t.history_completed_at ?? t.completed_at, ctx.timezone, now));
    }
  }

  const byDue = (a: Task, b: Task) => {
    const ia = info.get(a.id)!;
    const ib = info.get(b.id)!;
    if (ia.at && ib.at && +ia.at !== +ib.at) return +ia.at - +ib.at;
    if (ia.at && !ib.at) return -1;
    if (!ia.at && ib.at) return 1;
    if (PRIORITY_RANK[a.priority] !== PRIORITY_RANK[b.priority]) {
      return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    }
    return a.title.localeCompare(b.title);
  };

  const byCompleted = (a: Task, b: Task) => {
    const ia = completed.get(a.id)!;
    const ib = completed.get(b.id)!;
    if (ia.at && ib.at && +ia.at !== +ib.at) return +ib.at - +ia.at;
    if (ia.at && !ib.at) return -1;
    if (!ia.at && ib.at) return 1;
    return a.title.localeCompare(b.title);
  };

  const buckets = new Map<string, BacklogGroup>();
  const push = (
    key: string,
    label: string,
    tone: BacklogGroup['tone'],
    task: Task,
    color?: string,
  ) => {
    let group = buckets.get(key);
    if (!group) {
      group = { key, label, meta: '', tone, color, tasks: [] };
      buckets.set(key, group);
    }
    group.tasks.push(task);
  };

  if (sort === 'due') {
    for (const t of tasks) {
      const tone = info.get(t.id)!.tone;
      push(tone, DUE_GROUP_LABEL[tone], tone, t);
    }
  } else if (sort === 'completed') {
    for (const t of tasks) {
      const tone = completed.get(t.id)!.tone;
      push(tone, COMPLETED_GROUP_LABEL[tone], 'plain', t);
    }
  } else if (sort === 'effort') {
    for (const t of tasks) {
      const est = t.estimate_minutes;
      if (est == null || est <= 0) {
        push('No estimate', 'No estimate', 'plain', t);
        continue;
      }
      const bucket = EFFORT_BUCKETS.find((b) => est <= b.max)!;
      push(bucket.label, bucket.label, 'plain', t);
    }
  } else if (sort === 'priority') {
    for (const t of tasks) {
      push(t.priority, t.priority.charAt(0).toUpperCase() + t.priority.slice(1), 'plain', t);
    }
  } else if (sort === 'client') {
    const byId = new Map(ctx.clients.map((c) => [c.id, c]));
    for (const t of tasks) {
      const client = byId.get(t.client_id);
      push(
        `client-${t.client_id}`,
        client?.name ?? 'Unassigned',
        'plain',
        t,
        client?.color,
      );
    }
  } else {
    const byId = new Map(ctx.projects.map((p) => [p.id, p]));
    for (const t of tasks) {
      const project = t.project_id != null ? byId.get(t.project_id) : undefined;
      push(
        project ? `project-${project.id}` : 'no-project',
        project?.name ?? 'No project',
        'plain',
        t,
        project?.color ?? undefined,
      );
    }
  }

  const groups = [...buckets.values()];

  if (sort === 'due') {
    groups.sort((a, b) => TONE_RANK[a.tone as DueTone] - TONE_RANK[b.tone as DueTone]);
  } else if (sort === 'completed') {
    groups.sort(
      (a, b) =>
        COMPLETED_TONE_RANK[a.key as CompletedTone] -
        COMPLETED_TONE_RANK[b.key as CompletedTone],
    );
  } else if (sort === 'effort') {
    const order = [...EFFORT_BUCKETS.map((b) => b.label), 'No estimate'];
    groups.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  } else if (sort === 'priority') {
    const order: Task['priority'][] = ['urgent', 'high', 'normal', 'low'];
    groups.sort(
      (a, b) =>
        order.indexOf(a.key as Task['priority']) - order.indexOf(b.key as Task['priority']),
    );
  } else {
    // Client / project: alphabetical, with the catch-all bucket pinned last.
    const last = (g: BacklogGroup) => (g.key === 'no-project' || g.label === 'Unassigned' ? 1 : 0);
    groups.sort((a, b) => last(a) - last(b) || a.label.localeCompare(b.label));
  }

  for (const group of groups) {
    group.tasks.sort(sort === 'completed' ? byCompleted : byDue);
    const mins = totalMinutes(group.tasks);
    group.meta = mins > 0 ? `${group.tasks.length} · ${compactDuration(mins)}` : `${group.tasks.length}`;
  }

  return groups;
}
